/**
 * <TagBar> Component
 *
 * Displays a news bar that renders a clickable tag/category and
 * description section that is usually displayed on hero banners
 */
import PropTypes from 'prop-types';
/** @jsx jsx this comment is required for Storybook to render */
import { css, jsx } from '@emotion/core';
import {
  colors,
  durations,
  fontFamilies,
  fontSizes,
  fontWeights,
  rgba,
  unitConverter as uc,
} from '../../../styles/base';
import Text from '../../atoms/Text/Text';
import Link from '../../atoms/Link/Link';

const TagBar = ({ link, tagBarButtonText, tagBarText }) => {
  const containerCss = css`
    display: block;
    max-width: ${uc('400px')};
    margin-top: ${uc('50px')};
  `;

  const tagCss = css`
    display: inline-flex;
    align-items: center;
    max-width: ${uc('400px')};
    padding: ${uc('5px')};
    color: ${colors.white};
    background-color: rgba(${rgba(colors.darkBlue, 0.65)});
    border-radius: ${uc('25px')};
    transition: ${durations.medium};

    &:hover {
      background-color: rgba(${rgba(colors.darkBlue, 0.85)});
    }
  `;

  const tagButtonCss = css`
    display: inline-block;
    padding: ${uc('8px 12px')};
    color: ${colors.primary};
    font-weight: ${fontWeights.bold};
    font-size: ${fontSizes.twelve};
    font-family: ${fontFamilies.proximaSoft};
    white-space: nowrap;
    text-transform: uppercase;
    text-decoration: none;
    background-color: ${colors.white};
    border-radius: ${uc('18px')};
  `;

  const textCss = css`
    margin: 0 ${uc('10px')};
    margin-left: ${uc('10px')};
    color: ${colors.white};
    font-size: ${fontSizes.twelve};
  `;

  const renderTagBarContents = () => {
    if (!tagBarButtonText || !tagBarText) {
      return null;
    }
    return (
      <div css={tagCss}>
        <div css={tagButtonCss}>{tagBarButtonText}</div>
        <Text css={textCss}>{tagBarText}</Text>
      </div>
    );
  };

  const renderTagBar = () => {
    return link.link && link.link.length > 0 ? (
      <Link className="stop-propagation" to={link} css={containerCss}>
        {renderTagBarContents()}
      </Link>
    ) : (
      <div css={containerCss}>{renderTagBarContents()}</div>
    );
  };

  return renderTagBar();
};

TagBar.propTypes = {
  tagBarButtonText: PropTypes.string,
  tagBarText: PropTypes.string,
  link: PropTypes.shape({}),
};

TagBar.defaultProps = {
  tagBarButtonText: null,
  tagBarText: null,
  link: {},
};

export default TagBar;
